import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  ViewChild,
  signal,
  inject,
  computed,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { map, switchMap, tap } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';

import {
  BenefitManagementService,
  CMSService,
  EligibilityService,
  MaterialModule,
} from '@core';
import { LoaderService } from '@management';
import { AppCenterAnalytics, AppInsightsService } from '@services';
import {
  Clients,
  ClinicalReviewStatus,
  ConfigSettings,
  IClinicalReviewRequest,
  IClinicalReviewResponse,
  MemberDetails,
} from '@interfaces';

import { formatDate, searchClientId } from 'src/app/util';
import { AppConstants } from '../../../app.constants';

/*
 * Replace this import with the actual standalone pipe class/path
 * used by your application.
 */
import { SharedModule, TimeStampToDatePipe } from '@shared';
import { MatMenuTrigger } from '@angular/material/menu';
import { ReactiveFormsModule } from '@angular/forms';

@Component({
  selector: 'app-clinical-reviews',
  templateUrl: './clinical-reviews.component.html',
  styleUrls: ['./clinical-reviews.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SharedModule, MaterialModule, MatTableModule, ReactiveFormsModule],
  providers: [TimeStampToDatePipe],
})
export class ClinicalReviewsComponent implements OnInit, AfterViewInit {
  // Inject dependencies using inject() function
  private readonly router = inject(Router);
  public readonly eligibilityService = inject(EligibilityService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly benefitService = inject(BenefitManagementService);
  private readonly appInsightService = inject(AppInsightsService);
  private readonly loaderService = inject(LoaderService);
  private readonly cmsService = inject(CMSService);
  private readonly timeStampToDatePipe = inject(TimeStampToDatePipe);

  // Use takeUntilDestroyed for automatic cleanup
  private readonly destroyRef = inject(DestroyRef);

  public readonly selectedMemberDetails = signal<MemberDetails | null>(null);

  public readonly displayCoverageType = AppConstants.DISPLAY_COVERAGE_TYPE;

  public readonly relationShip = AppConstants.RELATIONSHIP_VALUES;

  public readonly totalLength = signal(0);

  public clientIdsWithoutPALatter!: string;

  public showBannerMessage = signal(false);

  public readonly displayedColumns: string[] = [
    'reviewId',
    'drugName',
    'memberName',
    'createDate',
    'status',
    'statusDescription',
    // 'documentUrl',
  ];

  public readonly dataSource = signal(
    new MatTableDataSource<ClinicalReviewStatus>([]),
  );

  @ViewChild('paginator')
  paginator!: MatPaginator;

  @ViewChild(MatSort)
  sort!: MatSort;

  @ViewChild('menuTrigger') menuTrigger!: MatMenuTrigger;

  public readonly clinicalReviewsResponse = signal<ClinicalReviewStatus[]>([]);

  public readonly showTable = signal(false);

  public currentPageIndex = signal(0);
  public limit = signal(5);

  // FILTER FEATURE - Commented out as per requirements
  // public yearsFilterOptions: number[] = [];
  // public yearFilterControl = new FormControl<number | null>(null);

  // Computed signal for filtered data count
  public readonly filteredDataCount = computed(() => {
    const dataSourceValue = this.dataSource();
    return dataSourceValue.filteredData?.length ?? 0;
  });

  constructor() {
  }

  public ngOnInit(): void {
    this.cmsService
      .getWordpressClientConfig()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (res: ConfigSettings) =>
          (this.clientIdsWithoutPALatter =
            res.ENABLE_CLINICAL_REVIEW_BANNER_MESSAGE),
      );

    this.appInsightService.trackPageView(AppCenterAnalytics.CLINICAL_REVIEWS);

    this.activatedRoute.queryParams
      .pipe(
        switchMap((queryParams) => {
          const request = {
            loading: true,
            ...queryParams,
          };

          return this.eligibilityService.getMemberDetailsById(request).pipe(
            map((response) =>
              Array.isArray(response) ? response[0] : response,
            ),
            tap((memberDetails) => {
              this.selectedMemberDetails.set(memberDetails);
              this.checkIfPALetterAvailableForClient();
            }),
            tap(() => {
              this.checkGroupLevelAccess();
            }),
            tap(() => {
              this.getClinicalReviews();
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  public ngAfterViewInit(): void {
    this.dataSource().paginator = this.paginator;
    this.dataSource().sort = this.sort;
  }

  public getClinicalReviews(startYear?: number): void {
    const memberDetails = this.selectedMemberDetails();

    if (!memberDetails) {
      return;
    }

    // Start date = Jan 1 of (current year - 2)
    let today = new Date();

    const startDate = new Date(today.getFullYear() - 2, 0, 1);

    // FILTER FEATURE - Commented out as per requirements
    // if (startYear) {
    //   startDate.setFullYear(startYear);
    //   today.setFullYear(startYear);

    //   if (startYear === new Date().getFullYear()) {
    //     // Use today's actual date
    //     today = new Date();
    //   } else {
    //     // Set to 31 Dec of the selected year
    //     today = new Date(startYear, 11, 31); // Month is 0-based, so 11 = Dec
    //   }
    // }
    // this.yearsFilterOptions = !startYear
    //   ? generateRange(startDate.getFullYear(), today.getFullYear())
    //   : this.yearsFilterOptions;

    const limit = this.limit();
    const page = this.currentPageIndex() + 1;
    const request: IClinicalReviewRequest = {
      clientId: memberDetails.clientId,
      cardId: memberDetails.cardId ?? '',
      personCode: memberDetails.personCode ?? '',
      searchDateStart: formatDate(startDate) as string,
      searchDateEnd: formatDate(today) as string,
      limit: limit,
      page: page,
    };

    this.loaderService.showLoader();

    this.eligibilityService
      .getClinicalReviews(request)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: IClinicalReviewResponse) => {
          this.showTable.set(true);
          this.loaderService.hideLoader();

          if (!response || !response.priorAuthorizations) {
            return;
          }

          this.appInsightService.trackPageView(
            AppCenterAnalytics.CLINICAL_REVIEWS_API_SUCCESS,
          );

          this.clinicalReviewsResponse.set(response.priorAuthorizations);

          const displayData = [...response.priorAuthorizations].sort(
            (firstReview: ClinicalReviewStatus, secondReview: ClinicalReviewStatus) =>
              new Date(
                secondReview.createdDate || secondReview.statusChangeDate,
              ).valueOf() -
              new Date(
                firstReview.createdDate || firstReview.statusChangeDate,
              ).valueOf(),
          );

          const tableDataSource = new MatTableDataSource(displayData);

          this.configureFilterPredicate(tableDataSource);
          tableDataSource.filter = this.dataSource().filter;

          this.dataSource.set(tableDataSource);

          this.totalLength.set(response.pagination.totalCount);
          this.setUpPagination();
        },

        error: (error) => {
          this.appInsightService.trackPageView(
            AppCenterAnalytics.CLINICAL_REVIEWS_API_ERROR,
            {
              Response: error,
            },
          );

          this.loaderService.hideLoader();
        },
      });
  }

  private configureFilterPredicate(
    tableDataSource: MatTableDataSource<ClinicalReviewStatus>,
  ): void {
    tableDataSource.filterPredicate = (
      review: ClinicalReviewStatus,
      filter: string,
    ): boolean => {
      const normalizedFilter = filter.trim().toLowerCase();
      const displayDate = this.getDisplayDate(review);
      const formattedDate = displayDate
        ? this.timeStampToDatePipe.transform(displayDate) || ''
        : '';

      const searchableText = [
        review.authNumber,
        review.drug,
        review.patient?.firstName,
        review.patient?.middleName,
        review.patient?.lastName,
        formattedDate,
        review.status,
        review.statusDescription,
      ]
        .filter((value) => value !== null && value !== undefined)
        .join(' ')
        .toLowerCase();

      return searchableText.includes(normalizedFilter);
    };
  }

  private setUpPagination(): void {
    setTimeout(() => {
      this.paginator.length = this.totalLength();
    }, 100);
  }

  private checkGroupLevelAccess(): void {
    const memberDetails = this.selectedMemberDetails();

    if (!memberDetails) {
      return;
    }

    this.benefitService
      .getClientsListByAccess()
      .pipe(
        tap((clients: Clients[]) => {
          const selectedClient = clients.find(
            (client: Clients) => client.clientId === memberDetails.clientId,
          );

          this.eligibilityService.updateIsMigratedValue(
            selectedClient?.migrationFlag === AppConstants.UTILS.Y,
          );

          this.eligibilityService.updateIsGroupLevelAccessValue(
            selectedClient?.groupLevelFlag ?? false,
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  // public processFile(docUrl: string, inquiryId: number, action: string): void {
  //   const url = new URL(docUrl);
  //   if (url.search) {
  //     const queryParams = {};
  //     url.searchParams.forEach((value, key) => {
  //       queryParams[key] = value;
  //     });
  //     this.loaderService.showLoader();
  //     const reqBody: IClinicalReviewDocRequest = {
  //       documentName: queryParams['documentName'],
  //       documentUrl: queryParams['documentUrl'],
  //       documentType: queryParams['documentType'],
  //       migrationFlag: this.migratedFlag,
  //       accessLevel: this.accessLevel,
  //     };
  //     this.subscription.add(
  //       this.eligibilityService.getClinicalReviewDocument(reqBody).subscribe(
  //         (res: Blob) => {
  //           this.appInsightService.trackPageView(
  //             AppCenterAnalytics.CLINICAL_REVIEWS_DOC_API_SUCCESS,
  //           );
  //           const blob = new Blob([res], { type: 'application/pdf' });
  //           const fileURL = URL.createObjectURL(blob);
  //           if (action === AppConstants.UTILS.VIEW) {
  //             this.appInsightService.trackPageView(
  //               AppCenterAnalytics.CLINICAL_REVIEWS_PDF_VIEWED,
  //             );
  //             const newWindow = window.open(fileURL, '_blank');
  //             // Handle pop-up blockers gracefully
  //             if (
  //               !newWindow ||
  //               newWindow.closed ||
  //               typeof newWindow.closed === 'undefined'
  //             ) {
  //               alert('Please allow pop-ups for this site to view the PDF.');
  //             }
  //             this.loaderService.hideLoader();
  //           } else if (action === AppConstants.UTILS.DOWNLOAD) {
  //             this.appInsightService.trackPageView(
  //               AppCenterAnalytics.CLINICAL_REVIEWS_PDF_DOWNLOAD,
  //             );
  //             const anchor = document.createElement('a');
  //             anchor.download = 'Clinical Review ' + inquiryId;
  //             anchor.href = fileURL;
  //             anchor.click();
  //             this.loaderService.hideLoader();
  //           }
  //         },
  //         (error: string) => {
  //           this.appInsightService.trackPageView(
  //             AppCenterAnalytics.CLINICAL_REVIEWS_DOC_API_ERROR,
  //             { Response: error },
  //           );
  //           this.loaderService.hideLoader();
  //         },
  //       ),
  //     );
  //   } else {
  //     this.loaderService.hideLoader();
  //   }
  // }

  public onPageChange(pageEvent: PageEvent): void {
    this.currentPageIndex.set(pageEvent.pageIndex);
    this.limit.set(pageEvent.pageSize);
    // FILTER FEATURE - Commented out as per requirements
    // this.getClinicalReviews(this.yearFilterControl.value ?? undefined);
    this.getClinicalReviews();
  }

  public search(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;

    const tableDataSource = this.dataSource();

    tableDataSource.filter = filterValue.trim().toLowerCase();
    this.dataSource.set(tableDataSource);

    this.totalLength.set(tableDataSource.filteredData.length);
    if (filterValue.trim() === '') {
      this.totalLength.set(
        this.paginator?.length ?? tableDataSource.data.length,
      );
    }
  }

  public navigateBackToMemberSearch(): void {
    this.router.navigateByUrl('/management/online-eligibility');
  }

  public checkIfPALetterAvailableForClient(): void {
    const memberDetails = this.selectedMemberDetails();
    if (!memberDetails) {
      this.showBannerMessage.set(false);
      return;
    }
    this.showBannerMessage.set(
      searchClientId(this.clientIdsWithoutPALatter, memberDetails.clientId),
    );
  }

  public getDisplayDate(row: ClinicalReviewStatus): string {
    return row.statusChangeDate || row.createdDate || '';
  }

  // FILTER FEATURE - Commented out as per requirements
  // public closeFilter(): void {
  //   this.menuTrigger?.closeMenu();
  // }

  // public applyYearFilter(): void {
  //   const selectedYear = this.yearFilterControl.value;
  //   this.currentPageIndex.set(0);
  //   this.paginator.pageIndex = 0; // Reset to the first page when applying a new filter
  //   this.limit.set(5); // Reset to the default limit when applying a new filter
  //   this.getClinicalReviews(selectedYear ?? undefined);
  //   this.closeFilter();
  // }
}
